import { Link } from "react-router-dom";
import type { ArticleListItem } from "@/types/api";
import { CategoryPill } from "@/components/news/CategoryPill";
import { FreshnessBadge } from "@/components/news/FreshnessBadge";
import { articleLeadImage, VideoCardBadge } from "@/components/news/VideoCardBadge";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { formatRelativeTime } from "@/lib/utils";
import { getFreshnessKind } from "@/lib/freshness";

interface ArticleCardProps {
  article: ArticleListItem;
  featured?: boolean;
  horizontal?: boolean;
  /** Prioriza LCP (ex.: lead da primeira seção) */
  priority?: boolean;
}

export function ArticleCard({
  article,
  featured = false,
  horizontal = false,
  priority = false,
}: ArticleCardProps) {
  const image = articleLeadImage(article);

  if (horizontal) {
    return (
      <article className="group overflow-hidden rounded border border-brand-outline-variant/50 bg-brand-surface-lowest transition-transform duration-200 ease-out will-change-transform hover:translate-x-0.5">
        <Link to={`/materia/${article.slug}`} className="flex gap-3 p-3 sm:gap-4 sm:p-0">
          {image ? (
            <div className="relative h-24 w-36 shrink-0 sm:h-auto sm:w-48 sm:self-stretch">
              <OptimizedImage
                src={image}
                alt={article.title}
                aspectRatio="16 / 10"
                wrapperClassName="h-24 w-36 rounded sm:h-full sm:w-48 sm:rounded-none"
                sizes="(max-width: 640px) 144px, 192px"
                width={192}
                height={120}
                priority={priority}
              />
              {article.has_video ? <VideoCardBadge compact /> : null}
            </div>
          ) : null}
          <div className="min-w-0 flex-1 sm:py-3 sm:pr-4">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <CategoryPill category={article.category} />
              <FreshnessBadge article={article} />
            </div>
            <h3 className="line-clamp-2 font-display text-base font-semibold leading-snug text-brand-ink group-hover:text-brand-primary sm:text-lg">
              {article.title}
            </h3>
            {article.summary ? (
              <p className="mt-1 line-clamp-2 font-serif text-sm text-brand-on-surface-variant">
                {article.summary}
              </p>
            ) : null}
            {!getFreshnessKind(article) ? (
              <p className="mt-2 text-xs uppercase tracking-wide text-brand-outline">
                {formatRelativeTime(article.publish_at || article.created_at)}
              </p>
            ) : null}
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group overflow-hidden rounded border border-brand-outline-variant/50 bg-brand-surface-lowest transition-transform duration-200 ease-out will-change-transform hover:-translate-y-0.5">
      <Link to={`/materia/${article.slug}`} className="block">
        <div className={featured ? "relative aspect-[16/9]" : "relative aspect-[16/10]"}>
          {image ? (
            <OptimizedImage
              src={image}
              alt={article.title}
              aspectRatio={featured ? "16 / 9" : "16 / 10"}
              wrapperClassName="h-full w-full rounded-none"
              priority={priority}
              sizes={
                featured
                  ? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 520px"
                  : "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 360px"
              }
              width={featured ? 640 : 480}
              height={featured ? 360 : 300}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-brand-primary/10 font-display text-brand-primary">
              Eusebio News
            </div>
          )}
          {article.has_video ? <VideoCardBadge /> : null}
        </div>
        <div className="space-y-2 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryPill category={article.category} />
            <FreshnessBadge article={article} />
          </div>
          <h3
            className={
              featured
                ? "line-clamp-3 min-h-[4.5em] font-display text-balance text-lg font-bold leading-snug text-brand-ink group-hover:text-brand-primary"
                : "line-clamp-3 min-h-[3.75em] font-display text-balance text-base font-semibold leading-snug text-brand-ink group-hover:text-brand-primary"
            }
          >
            {article.title}
          </h3>
          <p className="line-clamp-2 min-h-[2.5em] font-serif text-sm text-brand-on-surface-variant">
            {article.summary || "\u00a0"}
          </p>
          {!getFreshnessKind(article) ? (
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-brand-outline">
              <span>{formatRelativeTime(article.publish_at || article.created_at)}</span>
            </div>
          ) : null}
        </div>
      </Link>
    </article>
  );
}
